/**
 * StatCard — eDMS Design System (UX bolder-refresh, primitive #4)
 * The elevated KPI tile: a large value (animated count-up), an optional trend
 * delta (▲ green / ▼ red, polarity-aware), an optional inline sparkline, and an
 * optional gradient accent spine. Direction: UX_REFRESH_DIRECTION.md §3.
 *
 * A11y: the animated number is aria-hidden; a visually-hidden summary carries the
 * settled value (+ delta) so a screen reader announces it once, not per frame.
 */
import { useId, type ReactNode } from 'react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import { clsx } from 'clsx';
import { useCountUp } from '../../hooks/useCountUp';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import styles from './StatCard.module.css';

export interface StatCardProps {
  /** Metric label, e.g. "Open deviations". */
  label: string;
  /** The value. number → animated count-up; string → shown verbatim. */
  value: number | string;
  /** Text before the value (e.g. currency symbol). */
  prefix?: string;
  /** Text after the value (e.g. "%", "days"). */
  suffix?: string;
  /** Decimals for the count-up + display. Default 0. */
  decimals?: number;
  /** Period-over-period delta. Sign drives the arrow; polarity drives the colour. */
  delta?: number;
  /** 'up-good' (default): rising = green. 'up-bad': rising = red (e.g. defects). */
  deltaPolarity?: 'up-good' | 'up-bad';
  /** Format the delta number. Default: rounded, e.g. "12.3". */
  deltaFormatter?: (delta: number) => string;
  /** Caption next to the delta, e.g. "vs last month". */
  deltaCaption?: string;
  /** Inline sparkline series (mini area chart). */
  sparkline?: number[];
  /** Accent colour for the spine + sparkline. Default brand purple. */
  accent?: string;
  /** Leading icon/element. */
  icon?: ReactNode;
  /** Paint the whole tile with the brand gradient (hero stat). Default false. */
  gradient?: boolean;
  /** Makes the tile an interactive button. */
  onClick?: () => void;
  className?: string;
}

const DEFAULT_ACCENT = 'var(--spectro-purple, #9333EA)';

export function StatCard({
  label,
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  delta,
  deltaPolarity = 'up-good',
  deltaFormatter,
  deltaCaption,
  sparkline,
  accent = DEFAULT_ACCENT,
  icon,
  gradient = false,
  onClick,
  className,
}: StatCardProps) {
  const reduced = usePrefersReducedMotion();
  const isNumeric = typeof value === 'number';
  const counted = useCountUp(isNumeric ? (value as number) : 0, { decimals, enabled: isNumeric });
  const sparkId = useId().replace(/:/g, '');

  const displayValue = isNumeric
    ? counted.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : String(value);
  const settledValue = isNumeric
    ? (value as number).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : String(value);

  const isUp = delta != null && delta > 0;
  const isDown = delta != null && delta < 0;
  const negative = deltaPolarity === 'up-bad' ? isUp : isDown;
  const arrow = isUp ? '▲' : isDown ? '▼' : '';
  const deltaText =
    delta != null ? (deltaFormatter ? deltaFormatter(delta) : `${Math.abs(delta)}`) : '';

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={clsx(styles.card, gradient && styles.gradient, onClick && styles.interactive, className)}
      style={{ ['--accent' as string]: accent }}
    >
      <span className={styles.spine} aria-hidden="true" />
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        {icon && <span className={styles.icon} aria-hidden="true">{icon}</span>}
      </div>

      <div className={styles.valueRow}>
        <span className={styles.value} aria-hidden="true">
          {prefix}
          {displayValue}
          {suffix}
        </span>
      </div>

      {(delta != null || deltaCaption) && (
        <div className={styles.deltaRow} aria-hidden="true">
          {delta != null && (
            <span className={clsx(styles.delta, negative ? styles.deltaDown : isUp || isDown ? styles.deltaUp : styles.deltaFlat)}>
              {arrow && <span className={styles.deltaArrow}>{arrow}</span>}
              {deltaText}
            </span>
          )}
          {deltaCaption && <span className={styles.deltaCaption}>{deltaCaption}</span>}
        </div>
      )}

      {sparkline && sparkline.length > 1 && (
        <div className={styles.spark} aria-hidden="true">
          <ResponsiveContainer width="100%" height={40}>
            <AreaChart data={sparkline.map((v, i) => ({ i, v }))} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={sparkId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={1.5} fill={`url(#${sparkId})`} isAnimationActive={!reduced} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <span className={styles.srOnly}>
        {label}: {prefix}{settledValue}{suffix}
        {delta != null && `, ${isUp ? 'up' : isDown ? 'down' : 'no change'} ${deltaText}${deltaCaption ? ` ${deltaCaption}` : ''}`}
      </span>
    </Tag>
  );
}
